/** @format */
import {useEffect, useState} from 'react';

import {add, format} from 'date-fns';

import {Grid2 as Grid, useMediaQuery} from '@mui/material';

import {useTheme} from '@mui/material/styles';

import {StatState} from 'types';

import {isTrialSubscription} from 'features/tariffs/helpers/isTrialSubscription';

import {getDaysLeft} from 'features/tariffs/helpers/getDaysLeft';

import {parseEndDate} from 'features/tariffs/helpers/parseEndDate';

import {formatDateToString} from 'helpers/dateHelpers';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import ExpenditureWidget from './components/ExpenditureWidget';
import PredictionWidget from './components/PredictionWidget';
import SavingsWidget from './components/SavingsWidget';
import {BudgetWidgetsDataProvider} from './hooks/BudgetWidgetsDataContext';
import {AdditionalText} from './styles';
import {DigitalCastButton} from './components/DigitalCastButton';
import {useGetHotspotSettingsQuery, useUpsertHotspotsStateMutation} from './graphql/__generated__';
import {HotspotLayoutBig} from './components/Hotspots/layouts/LayoutBig';
import {HotspotLayoutSmall} from './components/Hotspots/layouts/LayoutSmall';
import {HotspotKey, HotspotStatusList} from './types';
import {BLOCK_HEIGHT, BLOCK_HEIGHT_COMPACT, STATUS_LIST_DEFAULT} from './constants';
import {getMutationKey} from './helpers/getMutationKey';
import {MEDIAPLAN_LOOKBACK_YEARS} from '../../constants';
import {getFilteredKeys} from './components/Hotspots/helpers/getFilteredKeys';

interface Company {
  id: string;
  uuid: string;
}

interface Props<T> {
  tokenStatus: StatState | null;
  currentCompany: T;
}

const BudgetAndExpese = <T extends Company>({tokenStatus, currentCompany}: Props<T>) => {
  const {id: companyId, uuid: companyUuid} = currentCompany;
  const [statusList, setStatusList] = useState<HotspotStatusList>(STATUS_LIST_DEFAULT);

  const {mixpanel} = useMixpanelContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [upsertHotspotsState] = useUpsertHotspotsStateMutation();

  const {data, loading} = useGetHotspotSettingsQuery({
    variables: {
      companyId,
      startDate: format(add(new Date(), {years: MEDIAPLAN_LOOKBACK_YEARS}), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
    },
    skip: !companyId,
  });

  // ВАЖНО: По умолчанию параметры готовности должны находиться в true.
  // Это нужно, чтобы при заходе на страницу юзер не увидел лишнюю верстку.
  // Поэтому мы используем здесь сравнение с loading.
  const isMediaplanReady = loading || Boolean(data?.currentUser?.currentCompany?.mediaplans.length);
  const isFunnelReady =
    loading || Boolean(data?.currentUser?.currentCompany?.funnelSettings.goals.length);
  const isFeedsReady = loading || Boolean(data?.currentUser?.company?.trackingFeeds?.length);
  const displayStatuses = data?.currentUser?.currentCompany?.hotspotsState;
  const isSensorsReady =
    loading || Boolean(data?.currentUser?.currentCompany?.hotspotsState?.sensorsTouched);

  useEffect(() => {
    setStatusList({
      mediaplan: {
        isDisplay: Boolean(!displayStatuses?.mediaplanHideAt),
        isReady: isMediaplanReady,
      },
      funnel: {
        isDisplay: Boolean(!displayStatuses?.funnelHideAt),
        isReady: isFunnelReady,
      },
      feeds: {
        isDisplay: Boolean(!displayStatuses?.feedHideAt),
        isReady: isFeedsReady,
      },
      sensors: {
        isDisplay: Boolean(!displayStatuses?.sensorsHideAt),
        isReady: isSensorsReady,
      },
    });
  }, [isFeedsReady, isFunnelReady, isMediaplanReady, isSensorsReady, displayStatuses]);

  const isTrial = isTrialSubscription(data?.currentUser?.subscription);

  const {isValid: isValidDate, parsedEndDate} = parseEndDate(
    data?.currentUser?.subscription.endDate,
  );
  const daysLeft = isValidDate ? getDaysLeft(parsedEndDate) : Infinity;

  const filteredKeys = getFilteredKeys(statusList);

  const showNavBlock = filteredKeys.length > 0;

  const digitalCastButtonHeight = showNavBlock && !isMobile ? BLOCK_HEIGHT : BLOCK_HEIGHT_COMPACT;

  const updateDisplayStatus = async (key: HotspotKey, isDisplay: boolean) => {
    setStatusList(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        isDisplay,
      },
    }));

    const mutationKey = getMutationKey(key);
    if (!mutationKey) return;

    const showStatus = isDisplay ? 'show' : 'hide';
    const trackName = `hotspot_${key}_${showStatus}`;
    mixpanel.track(trackName, {project_uuid: companyUuid});

    await upsertHotspotsState({
      variables: {
        companyId,
        input: {
          [mutationKey]: isDisplay ? null : formatDateToString(new Date(), 'yyyy-MM-dd'),
        },
      },
    });
  };

  const handleSetupHotspot = (key: HotspotKey) => {
    mixpanel.track(`hotspot_${key}_settings`, {project_uuid: companyUuid});
  };

  return (
    <BudgetWidgetsDataProvider>
      {tokenStatus === 'NOT_READY' && (
        <AdditionalText>Проект настроен, данные скоро появятся</AdditionalText>
      )}

      <Grid container spacing={2}>
        <Grid size={{xs: 12, sm: 5, lg: 2.5}}>
          <ExpenditureWidget mode='month' tokenStatus={tokenStatus} />
        </Grid>
        <Grid size={{xs: 12, sm: 7, lg: 2.5}}>
          <ExpenditureWidget mode='yesterday' tokenStatus={tokenStatus} />
        </Grid>
        <Grid size={{xs: 12, sm: 5, lg: 2.5}}>
          <SavingsWidget tokenStatus={tokenStatus} />
        </Grid>
        <Grid size={{xs: 12, sm: 7, lg: 4.5}}>
          <PredictionWidget tokenStatus={tokenStatus} />
        </Grid>
        {showNavBlock && (
          <Grid size={{xs: 12, sm: 'auto'}}>
            <HotspotLayoutSmall
              statusList={statusList}
              filteredKeys={filteredKeys}
              onExpand={key => updateDisplayStatus(key, true)}
              companyId={companyId}
              daysLeft={daysLeft}
              isTrial={isTrial}
              onSetup={handleSetupHotspot}
            />
          </Grid>
        )}
        <Grid size={{xs: 12, sm: 'grow'}}>
          <DigitalCastButton overrideHeight={digitalCastButtonHeight} />
        </Grid>
      </Grid>

      <HotspotLayoutBig
        statusList={statusList}
        onHide={key => updateDisplayStatus(key, false)}
        companyId={companyId}
        daysLeft={daysLeft}
        isTrial={isTrial}
        onSetup={handleSetupHotspot}
      />
    </BudgetWidgetsDataProvider>
  );
};

export default BudgetAndExpese;
